import { logger } from '../../utils/logger';
/**
 * Dashboard Slice
 * Redux Toolkit slice for dashboard widget layout management
 *
 * Features:
 * - Widget layout state management with explicit x/y/w/h grid coordinates
 * - LocalStorage persistence
 * - 2D drag-and-drop placement (react-grid-layout)
 * - Reset to default layout
 */

import { createSlice, PayloadAction, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { WidgetLayout, DashboardLayoutState } from '@creatoros/shared';
import { getDefaultLayout, WIDGET_REGISTRY } from '../../config/widgetRegistry';
import { dashboardService } from '../../services/dashboard.service';

/**
 * Constants
 */
const STORAGE_KEY_PREFIX = 'dashboard_layout_';
const LAYOUT_VERSION = 7; // Bumped to reset to new default 9-widget layout

/**
 * Load layout from localStorage
 */
const loadLayoutFromStorage = (userId?: string): DashboardLayoutState | null => {
  if (!userId) return null;

  try {
    const key = STORAGE_KEY_PREFIX + userId;
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed: DashboardLayoutState = JSON.parse(stored);

    // Version check - if version mismatch, reset to default
    if (parsed.version !== LAYOUT_VERSION) {
      logger.debug('[Dashboard] Layout version mismatch, resetting to default');
      return null;
    }

    return parsed;
  } catch (error) {
    logger.error('[Dashboard] Failed to load layout from storage:', error);
    return null;
  }
};

/**
 * Save layout to localStorage
 */
const saveLayoutToStorage = (userId: string, state: DashboardLayoutState): void => {
  try {
    const key = STORAGE_KEY_PREFIX + userId;
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    logger.error('[Dashboard] Failed to save layout to storage:', error);
  }
};

/**
 * Initial State
 */
const initialState: DashboardLayoutState = {
  widgets: getDefaultLayout(),
  version: LAYOUT_VERSION,
  isCustomized: false,
  updatedAt: new Date().toISOString(),
};

/**
 * Async thunk to sync layout to backend
 */
export const syncLayoutToBackend = createAsyncThunk(
  'dashboard/syncToBackend',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = (getState() as RootState).dashboard;
      const result = await dashboardService.saveLayout(state);
      return result;
    } catch (error: any) {
      logger.error('[Dashboard] Failed to sync to backend:', error);
      return rejectWithValue(error.message || 'Failed to sync layout');
    }
  }
);

/**
 * Dashboard Slice
 */
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    /**
     * Initialize layout (call on app load with userId)
     */
    initializeLayout: (state, action: PayloadAction<{ userId: string }>) => {
      const stored = loadLayoutFromStorage(action.payload.userId);
      if (stored) {
        state.widgets = stored.widgets;
        state.version = stored.version;
        state.isCustomized = stored.isCustomized;
        state.updatedAt = stored.updatedAt;
        if (stored.lastSyncedAt) {
          state.lastSyncedAt = stored.lastSyncedAt;
        }
      }
      // If no stored layout, keep initialState (default layout)
    },

    /**
     * Update widget grid positions (called after react-grid-layout drag/drop or resize)
     * Accepts the layout items array from onLayoutChange.
     */
    updateLayout: (
      state,
      action: PayloadAction<{
        items: Array<{ i: string; x: number; y: number; w: number; h: number }>;
        userId: string;
      }>
    ) => {
      const { items, userId } = action.payload;
      const positionMap = new Map(items.map((item) => [item.i, item]));

      state.widgets = state.widgets.map((widget) => {
        const pos = positionMap.get(widget.id);
        if (!pos) return widget;
        return { ...widget, x: pos.x, y: pos.y, w: pos.w, h: pos.h };
      });

      state.isCustomized = true;
      state.updatedAt = new Date().toISOString();
      saveLayoutToStorage(userId, state);
    },

    /**
     * Toggle widget visibility
     */
    toggleWidgetVisibility: (
      state,
      action: PayloadAction<{ widgetId: string; userId: string }>
    ) => {
      const widget = state.widgets.find((w) => w.id === action.payload.widgetId);
      if (widget) {
        widget.visible = !widget.visible;
        state.isCustomized = true;
        state.updatedAt = new Date().toISOString();
        saveLayoutToStorage(action.payload.userId, state);
      }
    },

    /**
     * Reset to default layout
     */
    resetLayout: (state, action: PayloadAction<{ userId: string }>) => {
      state.widgets = getDefaultLayout();
      state.isCustomized = false;
      state.updatedAt = new Date().toISOString();
      delete state.lastSyncedAt;
      saveLayoutToStorage(action.payload.userId, state);
    },

    /**
     * Set enabled widgets (bulk visibility update from widget selector)
     */
    setEnabledWidgets: (
      state,
      action: PayloadAction<{ enabledWidgetIds: string[]; userId: string }>
    ) => {
      const { enabledWidgetIds, userId } = action.payload;
      const enabledSet = new Set(enabledWidgetIds);

      // Update visibility for all widgets based on enabled list
      state.widgets.forEach((widget) => {
        widget.visible = enabledSet.has(widget.id);
      });

      state.isCustomized = true;
      state.updatedAt = new Date().toISOString();
      saveLayoutToStorage(userId, state);
    },

    /**
     * Clear layout state (on logout)
     */
    clearLayout: () => initialState,
  },
});

/**
 * Selectors
 */

// Base selector
export const selectDashboardState = (state: RootState) => state.dashboard;

// Get sorted visible widgets — sorted by row (y) then column (x)
export const selectVisibleWidgets = createSelector([selectDashboardState], (dashboardState) => {
  const registryTypes = new Set(WIDGET_REGISTRY.map((w) => w.type));
  return dashboardState.widgets
    .filter((w) => w.visible && registryTypes.has(w.type))
    .sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
});

// Get all widgets (including hidden)
export const selectAllWidgets = (state: RootState): WidgetLayout[] => {
  return state.dashboard.widgets;
};

// Check if layout is customized
export const selectIsLayoutCustomized = (state: RootState): boolean => {
  return state.dashboard.isCustomized;
};

// Get layout update timestamp
export const selectLayoutUpdatedAt = (state: RootState): string => {
  return state.dashboard.updatedAt;
};

// Get layout sync timestamp
export const selectLayoutLastSyncedAt = (state: RootState): string | undefined => {
  return state.dashboard.lastSyncedAt;
};

// Get IDs of enabled (visible) widgets
export const selectEnabledWidgetIds = createSelector([selectDashboardState], (dashboardState) => {
  return dashboardState.widgets.filter((w) => w.visible).map((w) => w.id);
});

/**
 * Export actions and reducer
 */
export const {
  initializeLayout,
  updateLayout,
  toggleWidgetVisibility,
  resetLayout,
  setEnabledWidgets,
  clearLayout,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
